import React, { useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { ArrowLeft, Loader2, Target, Footprints, Beef, Flame, Settings as SettingsIcon, AlertTriangle, TrendingDown } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { useAuth } from '../../contexts/AuthContext'
import { isOwner } from '../../lib/owner'
import { useUnits } from '../../contexts/UnitsContext'
import { useGoalPace } from '../../hooks/useGoalPace'
import { cn } from '../../lib/utils'

const toneText = { green: 'text-emerald-500', amber: 'text-amber-500', neutral: 'text-muted-foreground' }
const toneBg = { green: 'bg-emerald-500/10', amber: 'bg-amber-500/10', neutral: 'bg-muted' }

function shortWeek(key) {
  return new Date(`${key}T00:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
}

export function GoalPacePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { weightUnit, displayValueWeight } = useUnits()
  const gp = useGoalPace()
  const [showSettings, setShowSettings] = useState(false)

  if (user && !isOwner(user)) return <Navigate to="/" replace />

  const w = (kg) => (kg == null ? '—' : displayValueWeight(kg).toFixed(1))
  const rate = (kg) => (kg == null ? '—' : displayValueWeight(Math.abs(kg)).toFixed(2))

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-1">
        <button type="button" onClick={() => navigate('/')} aria-label={t('settings.back')} className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground">
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">{t('goalPace.title')}</h1>
        <button type="button" onClick={() => setShowSettings((v) => !v)} aria-label={t('goalPace.settings')} className="p-2 rounded-md hover:bg-accent text-muted-foreground">
          <SettingsIcon className="w-5 h-5" />
        </button>
      </div>

      {showSettings && <SettingsForm gp={gp} weightUnit={weightUnit} t={t} onClose={() => setShowSettings(false)} />}

      {gp.loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : gp.pace.phase === 'baseline' ? (
        <BaselineState count={gp.pace.weighInCount} t={t} />
      ) : (
        <>
          <HeadlineCard gp={gp} w={w} rate={rate} weightUnit={weightUnit} t={t} />
          <Guardrails gp={gp} w={w} rate={rate} weightUnit={weightUnit} t={t} />
          <PaceChart chart={gp.chart} displayValueWeight={displayValueWeight} weightUnit={weightUnit} t={t} />
        </>
      )}

      <LeversPanel gp={gp} t={t} />
    </div>
  )
}

function HeadlineCard({ gp, w, rate, weightUnit, t }) {
  const { pace, rawToday } = gp
  const tone = pace.tone || 'neutral'
  const dir = pace.weeklyRate == null ? '' : pace.weeklyRate > 0 ? '↓' : '↑'
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground" dir="auto">{t('goalPace.sevenDayAvg')}</span>
          <span className="text-[11px] text-app-primary-500 font-semibold" dir="auto">
            {pace.daysRemaining != null ? t('goalPace.daysLeft', { n: Math.max(0, pace.daysRemaining) }) : '—'}
          </span>
        </div>
        <div className="flex items-end gap-3">
          <span className="text-4xl font-bold text-foreground tabular-nums" dir="ltr">{w(pace.currentAvg)}</span>
          <span className="text-sm text-muted-foreground mb-1" dir="ltr">{weightUnit}</span>
          {rawToday && (
            <span className="text-xs text-muted-foreground mb-1.5 ms-auto" dir="auto">
              {t('goalPace.todayRaw', { v: `${w(rawToday.weight)} ${weightUnit}` })}
            </span>
          )}
        </div>
        <div className={cn('flex items-center justify-between rounded-xl px-3 py-2', toneBg[tone])}>
          <span className="text-xs text-muted-foreground" dir="auto">{t('goalPace.weeklyRate')}</span>
          <span className={cn('text-sm font-bold tabular-nums', toneText[tone])} dir="ltr">
            {pace.weeklyRate == null ? '—' : `${dir} ${rate(pace.weeklyRate)} ${weightUnit}/wk`}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground text-center" dir="auto">
          {t('goalPace.bandHint', { min: rate(pace.band.minKg), max: rate(pace.band.maxKg), unit: weightUnit })}
        </p>
        {pace.projectedWeight != null && (
          <p className="text-[12px] text-center" dir="auto">
            <TrendingDown className="inline w-3.5 h-3.5 me-1 text-app-primary-500" />
            <span className="text-muted-foreground">{t('goalPace.projected')} </span>
            <span className="font-semibold text-foreground" dir="ltr">{w(pace.projectedWeight)} {weightUnit}</span>
          </p>
        )}
      </CardContent>
    </Card>
  )
}

// Calm, informative guardrail notes. Never "faster = better", never "eat less".
function Guardrails({ gp, w, rate, weightUnit, t }) {
  const { pace } = gp
  const notes = []
  if (pace.tooFast2wk) notes.push({ key: 'tooFast', body: t('goalPace.note.tooFast') })
  if (pace.goal?.aggressive) {
    notes.push({
      key: 'aggressive',
      body: t('goalPace.note.aggressive', { weight: `${w(pace.goal.realisticWeight)} ${weightUnit}` }),
    })
  }
  if (pace.behind && !pace.tooFast2wk) notes.push({ key: 'behind', body: t('goalPace.note.behind') })
  if (!notes.length) return null
  return (
    <div className="space-y-2">
      {notes.map((n) => (
        <div key={n.key} className="flex items-start gap-2 rounded-xl bg-amber-500/10 px-3 py-2.5" dir="auto">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-foreground leading-relaxed">{n.body}</p>
        </div>
      ))}
    </div>
  )
}

function PaceChart({ chart, displayValueWeight, weightUnit, t }) {
  if (!chart || chart.length < 2) return null
  const data = chart.map((p) => ({
    label: shortWeek(p.weekEndKey),
    avg: displayValueWeight(p.avg),
    target: p.target != null ? displayValueWeight(p.target) : null,
  }))
  const Tip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
      <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-xs" dir="ltr">
        <p className="text-muted-foreground">{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey} style={{ color: p.color }}>{p.dataKey === 'avg' ? t('goalPace.avgLine') : t('goalPace.targetLine')}: {Number(p.value).toFixed(1)} {weightUnit}</p>
        ))}
      </div>
    )
  }
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2" dir="auto">{t('goalPace.chartTitle')}</p>
        <div dir="ltr" className="w-full">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }} stroke="#6B7280" />
              <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }} stroke="#6B7280" width={40} />
              <Tooltip content={<Tip />} />
              <Line type="monotone" dataKey="avg" stroke="hsl(var(--app-primary-500))" strokeWidth={2.5} dot={{ r: 2.5 }} activeDot={{ r: 5 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="target" stroke="#10b981" strokeWidth={1.5} strokeDasharray="4 4" dot={false} isAnimationActive={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex items-center gap-4 text-[11px] text-muted-foreground" dir="auto">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: 'hsl(var(--app-primary-500))' }} />{t('goalPace.avgLine')}</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-emerald-500" />{t('goalPace.targetLine')}</span>
        </div>
      </CardContent>
    </Card>
  )
}

function LeversPanel({ gp, t }) {
  const { steps, protein, setStepsForDay } = gp
  const [stepDraft, setStepDraft] = useState(steps?.today != null ? String(steps.today) : '')
  return (
    <div className="space-y-2">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-1" dir="auto">{t('goalPace.levers')}</p>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Footprints className="w-4 h-4 text-app-primary-500" />
            <span className="text-sm font-semibold text-foreground" dir="auto">{t('goalPace.steps')}</span>
            <span className="text-[11px] text-muted-foreground ms-auto" dir="auto">{t('goalPace.stepTarget', { min: steps.min.toLocaleString(), max: steps.max.toLocaleString() })}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <Stat label={t('goalPace.today')} value={steps.today != null ? steps.today.toLocaleString() : '—'} />
            <Stat label={t('goalPace.avg7')} value={steps.avg7 != null ? Math.round(steps.avg7).toLocaleString() : '—'} />
          </div>
          <div className="flex items-center gap-2">
            <input type="number" inputMode="numeric" value={stepDraft} onChange={(e) => setStepDraft(e.target.value)}
              placeholder={t('goalPace.logSteps')} dir="ltr"
              className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-foreground text-sm" />
            <button type="button" onClick={() => stepDraft && setStepsForDay(stepDraft)}
              className="h-10 px-4 rounded-md bg-app-primary-500 text-white text-sm font-medium">{t('common.save')}</button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Beef className="w-4 h-4 text-app-primary-500" />
            <span className="text-sm font-semibold text-foreground" dir="auto">{t('goalPace.protein')}</span>
            <span className="text-[11px] text-muted-foreground ms-auto" dir="auto">{t('goalPace.proteinTarget', { g: protein.target })}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label={t('goalPace.today')} value={protein.today != null ? `${Math.round(protein.today)} g` : '—'} />
            <Stat label={t('goalPace.avg7')} value={protein.avg7 != null ? `${Math.round(protein.avg7)} g` : '—'} />
            <Stat label={t('goalPace.streak')} value={<span className="inline-flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-amber-500" />{protein.streak}</span>} />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-muted/50 py-2">
      <p className="text-base font-bold text-foreground tabular-nums" dir="ltr">{value}</p>
      <p className="text-[10px] text-muted-foreground" dir="auto">{label}</p>
    </div>
  )
}

function BaselineState({ count, t }) {
  return (
    <Card>
      <CardContent className="p-6 text-center space-y-2">
        <Target className="w-8 h-8 text-app-primary-500 mx-auto" />
        <p className="text-sm font-semibold text-foreground" dir="auto">{t('goalPace.buildingBaseline')}</p>
        <p className="text-[12px] text-muted-foreground" dir="auto">{t('goalPace.buildingBaselineHint', { n: count, need: 7 })}</p>
      </CardContent>
    </Card>
  )
}

function SettingsForm({ gp, weightUnit, t, onClose }) {
  const s = gp.settings
  const [targetDate, setTargetDate] = useState(s.target_date || '')
  const [baseWeight, setBaseWeight] = useState(s.baseline_weight_kg != null ? String(s.baseline_weight_kg) : '')
  const [baseDate, setBaseDate] = useState(s.baseline_date || '')
  const [goalWeight, setGoalWeight] = useState(s.goal_weight_kg != null ? String(s.goal_weight_kg) : '')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    await gp.saveSettings({
      target_date: targetDate || undefined,
      baseline_weight_kg: baseWeight ? Number(baseWeight) : undefined,
      baseline_date: baseDate || undefined,
      goal_weight_kg: goalWeight ? Number(goalWeight) : null,
    })
    setBusy(false)
    onClose()
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <Field label={t('goalPace.targetDate')}>
          <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} dir="ltr"
            className="w-full h-10 px-2 rounded-md border border-input bg-background text-foreground text-sm" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('goalPace.baselineWeight', { unit: weightUnit })}>
            <input type="number" step="0.1" value={baseWeight} onChange={(e) => setBaseWeight(e.target.value)} dir="ltr"
              className="w-full h-10 px-2 rounded-md border border-input bg-background text-foreground text-sm" />
          </Field>
          <Field label={t('goalPace.baselineDate')}>
            <input type="date" value={baseDate} onChange={(e) => setBaseDate(e.target.value)} dir="ltr"
              className="w-full h-10 px-2 rounded-md border border-input bg-background text-foreground text-sm" />
          </Field>
        </div>
        <Field label={t('goalPace.goalWeight', { unit: weightUnit })}>
          <input type="number" step="0.1" value={goalWeight} onChange={(e) => setGoalWeight(e.target.value)} placeholder={t('goalPace.optional')} dir="ltr"
            className="w-full h-10 px-2 rounded-md border border-input bg-background text-foreground text-sm" />
        </Field>
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={busy} className="flex-1 h-10 rounded-md bg-app-primary-500 text-white text-sm font-medium inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t('common.save')}
          </button>
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md bg-muted text-foreground text-sm">{t('common.cancel')}</button>
        </div>
      </CardContent>
    </Card>
  )
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] text-muted-foreground" dir="auto">{label}</span>
      {children}
    </label>
  )
}

export default GoalPacePage
